import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { TextField, Button, Typography, Container, Paper, Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

const BackgroundContainer = styled(Box)({
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(62deg, #87CEEB, #00b4d8)',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '400px',
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.08)',
}));

const Form = styled('form')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: '20px',
  backgroundColor: '#00b4d8',
  color: '#FFFFFF',
  padding: theme.spacing(1.5),
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#0095b6',
  },
  '&:disabled': {
    backgroundColor: '#b0e0e6',
    color: '#FFFFFF',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#00b4d8',
    },
    '&:hover fieldset': {
      borderColor: '#0095b6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00b4d8',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#00b4d8',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00b4d8',
  },
}));

const logoUrl = "data:image/png;base64,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";

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatus('');
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const token = await user.getIdToken(true);
      console.log('=== TOKEN PARA POSTMAN ===');
      console.log('Bearer ' + token);
      console.log('========================');

      setStatus('Usuario autenticado. Obteniendo rol...');
      console.log("Usuario autenticado:", user.uid);

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const role = userData.role;

        setStatus(`Rol obtenido: ${role}. Redirigiendo...`);
        console.log("Rol del usuario:", role);

        localStorage.setItem('userRole', role);

        if (role === 'admin') {
          console.log("Redirigiendo a admin-dashboard");
          navigate('/');
        } else {
          setStatus(`Rol desconocido: ${role}`);
        }
      } else if (user.email) {
        setStatus(`Rol obtenido: driver. Redirigiendo...`);
        localStorage.setItem('userRole', 'driver');
        console.log("Redirigiendo a driver-dashboard!!");
        navigate('/');
      } else {
        setStatus("Documento de usuario no encontrado");
        console.log("Documento de usuario no encontrado");
        throw new Error('User document does not exist');
      }
    } catch (error) {
      console.error("Error en el login:", error);
      setError('Error al iniciar sesión. Verifica tus credenciales.');
      setStatus('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BackgroundContainer>
      <Container component="main" maxWidth="xs">
        <StyledPaper elevation={6}>
          <Box mb={3} display="flex" justifyContent="center">
            <img src={logoUrl} alt="Ollin Logo" width="120" />
          </Box>
          <Typography
            component="h1"
            variant="h5"
            color="textPrimary"
            gutterBottom
            style={{
              color: '#00b4d8',
              fontWeight: 'bold',
              marginBottom: '24px'
            }}
          >
            Iniciar Sesión
          </Typography>
          <Form onSubmit={handleSubmit}>
            <StyledTextField
              variant="outlined"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <StyledTextField
              variant="outlined"
              required
              fullWidth
              id="password"
              label="Contraseña"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            {error && (
              <Typography
                color="error"
                align="center"
                style={{
                  marginTop: '8px',
                  backgroundColor: '#ffebee',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                {error}
              </Typography>
            )}
            {status && (
              <Typography
                color="primary"
                align="center"
                style={{
                  marginTop: '8px',
                  backgroundColor: '#e6f2ff',
                  padding: '8px',
                  borderRadius: '8px'
                }}
              >
                {status}
              </Typography>
            )}
            <SubmitButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || !email || !password}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Iniciar Sesión'
              )}
            </SubmitButton>
          </Form>
        </StyledPaper>
      </Container>
    </BackgroundContainer>
  );
};

export default Login;